import {createPortal} from 'react-dom'
import {useId} from 'react'
import {X} from 'lucide-react'
import useDialogAccessibility from './useDialogAccessibility'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'md' | 'lg' | 'xl' | 'full'
  closeDisabled?: boolean
  description?: string
}

const sizeClasses = {
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]',
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'lg',
  closeDisabled = false,
  description,
}: ModalProps) {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useDialogAccessibility<HTMLDivElement>(isOpen, onClose, closeDisabled)
  if (!isOpen) return null

  return createPortal(
    <div className='devops-modal-root fixed inset-0 z-[1200] flex items-center justify-center p-3 sm:p-4 animate-fade-in'>
      <div
        className='fixed inset-0 bg-slate-900/50 backdrop-blur-sm'
        onClick={() => !closeDisabled && onClose()}
        aria-hidden='true'
      />
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Detalhes'}
        aria-describedby={description ? descriptionId : undefined}
        aria-busy={closeDisabled}
        tabIndex={-1}
        className={`devops-surface devops-modal-panel relative w-full ${sizeClasses[size]} max-h-[90dvh] flex flex-col animate-scale-in overflow-hidden outline-none`}
      >
        <div className='devops-modal-header flex items-start justify-between gap-4 px-5 sm:px-6 py-4 border-b border-slate-100 shrink-0'>
          <div className='min-w-0'>
            <h2 id={titleId} className='m-0 text-lg font-semibold text-slate-800'>
              {title || 'Detalhes'}
            </h2>
            {description && (
              <p id={descriptionId} className='m-0 mt-1 text-sm text-slate-500'>
                {description}
              </p>
            )}
          </div>
          <button
            type='button'
            onClick={onClose}
            disabled={closeDisabled}
            className='devops-icon-button shrink-0 disabled:opacity-40'
            aria-label='Fechar modal'
          >
            <X size={20} />
          </button>
        </div>
        <div className='devops-modal-content overflow-y-auto overscroll-contain min-h-0 flex-1'>
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
